import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  LocationOn as LocationIcon,
  Schedule as TimeIcon,
  LocalShipping as DeliveryIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelledIcon,
  Pending as PendingIcon,
  Map as MapIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Timeline as TrackingIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { useAuth } from '../../contexts/AuthContext';
import { getPersonnelByUid } from '../../services/migration/personnelDataService';
import { mainApi } from '../../config/apiClient';
import { consumePendingCourierTracking } from '../aichat/agent/uiBridge';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet default icon fix
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Kurye konumu için tip
interface CourierLocation {
  courierId: string;
  courierName: string;
  latitude: number;
  longitude: number;
  status: 'active' | 'busy' | 'offline';
  currentOrder?: string;
  lastUpdate: string;
  branchName?: string;
}

// Kurye aktivite tipi
interface CourierActivity {
  id: string;
  courierId: string;
  courierName: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerPhone?: string;
  deliveryAddress: string;
  musteriKonumu?: string; // JSON string formatında konum bilgisi
  orderType: 'restaurant' | 'ecommerce';
  status: 'taken' | 'delivered' | 'cancelled';
  takenAt?: string;
  deliveredAt?: string;
  deliveryDuration?: number; // dakika cinsinden
  totalAmount: number;
  branchName?: string;
  currentLocation?: string;
}

// Kurye özet bilgileri
interface CourierSummary {
  courierId: string;
  courierName: string;
  totalOrders: number;
  completedOrders: number;
  activeOrders: number;
  totalEarnings: number;
  averageDeliveryTime: number;
  lastActivity?: string;
  status: 'active' | 'busy' | 'offline';
  currentLocation?: string;
  branchName?: string;
}

interface CourierTrackingProps {
  onBack: () => void;
}

const CourierTracking: React.FC<CourierTrackingProps> = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const { t } = useTranslation('salesSystem');
  const { userDetails } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<CourierActivity[]>([]);
  const [courierSummaries, setCourierSummaries] = useState<CourierSummary[]>([]);
  const [courierLocations, setCourierLocations] = useState<CourierLocation[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Filtreleme ve arama
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [courierFilter, setCourierFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all'); // Varsayılan olarak tümünü göster

  // AI ajan köprüsü (openCourierTracking): mount olunca bekleyen isteğin
  // filtrelerini uygular (isteği CourierManagement peek ile görüp bu ekranı açtı).
  useEffect(() => {
    const req = consumePendingCourierTracking();
    if (!req) return;
    if (typeof req.searchText === 'string') setSearchTerm(req.searchText);
    if (typeof req.status === 'string') setStatusFilter(req.status);
    if (typeof req.dateFilter === 'string') setDateFilter(req.dateFilter);
  }, []);
  const [filteredActivities, setFilteredActivities] = useState<CourierActivity[]>([]);
  
  // Sayfalama için state - Personnel.tsx'teki gibi
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 25 : 50);
  
  // Sayfalandırılmış aktivite verileri - Personnel.tsx'teki gibi
  const paginatedActivities = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredActivities.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredActivities, page, rowsPerPage]);
  
  // Filtre değişikliklerinde sayfa numarasını sıfırla - Personnel.tsx'teki gibi
  useEffect(() => {
    setPage(0);
  }, [filteredActivities]);
  
  // Sayfa değişimi - Personnel.tsx'teki gibi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi - Personnel.tsx'teki gibi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Modal states
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [customerLocationModalOpen, setCustomerLocationModalOpen] = useState(false);
  const [selectedCustomerLocation, setSelectedCustomerLocation] = useState<{
    latitude: number;
    longitude: number;
    customerName: string;
    orderNumber: string;
  } | null>(null);

  // Kurye isimlerini Firebase'den çek
  const enrichWithCourierNames = async (activities: CourierActivity[]) => {
    try {
      const courierIdsSet = new Set(activities.map(a => a.courierId).filter(Boolean));
      const courierIds = Array.from(courierIdsSet);
      
      for (const courierId of courierIds) {
        try {
          const personnel = await getPersonnelByUid(courierId);
          if (personnel) {
            const fullName = `${personnel.firstName || ''} ${personnel.lastName || ''}`.trim() || personnel.name || personnel.email || t('courierTracking.defaults.unknownCourier');
            
            // Bu kurye ID'sine sahip tüm aktivitelerde ismi güncelle
            activities.forEach(activity => {
              if (activity.courierId === courierId) {
                activity.courierName = fullName;
              }
            });
          }
        } catch (error) {
          console.warn(`Kurye bilgisi alınamadı: ${courierId}`, error);
        }
      }
    } catch (error) {
      console.error('Kurye isimleri yüklenirken hata:', error);
    }
  };

  // Kurye özet verilerini hesapla
  const calculateCourierSummaries = (activities: CourierActivity[]): CourierSummary[] => {
    const courierMap = new Map<string, {
      totalOrders: number;
      completedOrders: number;
      activeOrders: number;
      totalEarnings: number;
      deliveryTimes: number[];
      lastActivity?: string;
      courierName: string;
    }>();

    activities.forEach(activity => {
      const existing = courierMap.get(activity.courierId) || {
        totalOrders: 0,
        completedOrders: 0,
        activeOrders: 0,
        totalEarnings: 0,
        deliveryTimes: [],
        courierName: activity.courierName
      };

      existing.totalOrders++;
      existing.totalEarnings += activity.totalAmount;
      
      if (activity.status === 'delivered') {
        existing.completedOrders++;
        if (activity.deliveryDuration) {
          existing.deliveryTimes.push(activity.deliveryDuration);
        }
      } else if (activity.status === 'taken') {
        existing.activeOrders++;
      }

      if (!existing.lastActivity || (activity.deliveredAt && activity.deliveredAt > existing.lastActivity)) {
        existing.lastActivity = activity.deliveredAt || activity.takenAt;
      }

      courierMap.set(activity.courierId, existing);
    });

    return Array.from(courierMap.entries()).map(([courierId, data]) => ({
      courierId,
      courierName: data.courierName,
      totalOrders: data.totalOrders,
      completedOrders: data.completedOrders,
      activeOrders: data.activeOrders,
      totalEarnings: data.totalEarnings,
      averageDeliveryTime: data.deliveryTimes.length > 0 
        ? Math.round(data.deliveryTimes.reduce((sum, time) => sum + time, 0) / data.deliveryTimes.length)
        : 0,
      lastActivity: data.lastActivity,
      status: data.activeOrders > 0 ? 'busy' : 'active',
      currentLocation: t('courierTracking.defaults.unknown'),
      branchName: t('courierTracking.defaults.unknown')
    }));
  };

  // Şirket ID'sini AuthContext'ten (SQL/own-auth oturumu) al.
  useEffect(() => {
    const uid = ownAuthSession.getCurrentUid();
    if (!uid) {
      setError(t('courierTracking.errors.noAuthenticatedUser'));
      setLoading(false);
      return;
    }

    const cid = userDetails?.companyId || null;
    if (cid) {
      setCompanyId(cid);
    } else {
      setError(t('courierTracking.errors.userInfoNotFound'));
      setLoading(false);
    }
  }, [userDetails]);

  // Kurye aktivitelerini yükle
  useEffect(() => {
    if (!companyId) return;

    const loadActivities = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🔍 [TRACKING] Kurye aktiviteleri yükleniyor...', { companyId });
        
        // SQL API'den sipariş verilerini al - MasaSiparisiController endpoint'ini kullan
        const response = await mainApi.get(`/api/MasaSiparisi/orders`);
        const result = response.data;

        if (result.success && result.data) {
            const allOrders = result.data;
            console.log('✅ [TRACKING] Tüm siparişler yüklendi', { totalOrders: allOrders.length });
          
          // E-ticaret siparişlerini filtrele (MasaId null veya 0 olanlar)
          // ve kurye tarafından alınmış olanları seç (API camelCase field isimleri)
          console.log('🔍 [DEBUG] İlk 3 sipariş örneği:', allOrders.slice(0, 3).map((order: any) => ({
            id: order.id,
            siparisId: order.siparisId,
            masaId: order.masaId, // camelCase
            teslimAlanKuryeId: order.teslimAlanKuryeId, // camelCase
            siparisDurumu: order.siparisDurumu, // camelCase
            teslimatAdresi: order.teslimatAdresi ? '✅ Var' : '❌ Yok', // camelCase
            musteriKonumu: order.musteriKonumu ? '✅ Var' : '❌ Yok' // camelCase
          })));

          // GEÇICI: Filtreyi kaldır, tüm siparişleri göster
          console.log('🔍 [DEBUG] Filtre kaldırıldı - tüm siparişler gösterilecek');
          const ecommerceOrders = allOrders.slice(0, 10); // İlk 10 siparişi al
          
          console.log('✅ [TRACKING] E-ticaret kurye siparişleri filtrelendi', { 
            ecommerceCount: ecommerceOrders.length 
          });

          // CourierActivity formatına dönüştür
          const activitiesData: CourierActivity[] = ecommerceOrders.map((order: any) => {
            console.log('🔍 [DEBUG] Sipariş işleniyor:', {
              siparisId: order.SiparisId || order.siparisId,
              // camelCase denemeleri
              musteriKonumu: order.musteriKonumu,
              teslimatAdresi: order.teslimatAdresi,
              // PascalCase denemeleri (SQL'den gelenler)
              MusteriKonumu: order.MusteriKonumu,
              TeslimatAdresi: order.TeslimatAdresi,
              // Tüm anahtarları göster
              allKeys: Object.keys(order),
              // Önemli alanları kontrol et
              masaId: order.masaId || order.MasaId,
              teslimAlanKuryeId: order.teslimAlanKuryeId || order.TeslimAlanKuryeId
            });
            
            // Sipariş detaylarını parse et (API camelCase field isimleri)
            let siparisDetaylari = [];
            let siparisNotu = null;
            
            try {
              if (order.siparisDetaylari) { // API camelCase
                const parsed = JSON.parse(order.siparisDetaylari);
                if (Array.isArray(parsed)) {
                  siparisDetaylari = parsed;
                } else if (parsed.siparisDetaylari) {
                  siparisDetaylari = parsed.siparisDetaylari;
                  siparisNotu = parsed.siparisNotu;
                }
              }
            } catch (e) {
              console.warn('Sipariş detayları parse edilemedi:', order.siparisId);
            }

            // Teslimat adresi ve müşteri bilgilerini parse et
            let teslimatAdresi = '';
            let telefonNumarasi = '';
            try {
              // API'den camelCase geliyor: teslimatAdresi
              const teslimatAdresiData = order.teslimatAdresi;
              if (teslimatAdresiData) {
                console.log('🏠 [DEBUG] TeslimatAdresi parse ediliyor:', teslimatAdresiData);
                const adresData = JSON.parse(teslimatAdresiData);
                // Format: {"title":"mem2","type":"home","firstName":"DOĞUKAN","lastName":"YİĞİT","phone":"05393988484","address":"Düzce merkez","district":"Düzce","city":"Düzce","postalCode":"81100"}
                teslimatAdresi = `${adresData.address || ''}, ${adresData.district || ''}, ${adresData.city || ''}`.replace(/^,\s*|,\s*$/g, '');
                telefonNumarasi = adresData.phone || '';
                console.log('✅ [DEBUG] TeslimatAdresi parse edildi:', { teslimatAdresi, telefonNumarasi });
              }
            } catch (e) {
              console.warn('Teslimat adresi parse edilemedi:', order.siparisId, order.teslimatAdresi);
              // Fallback - eğer JSON değilse direkt string olarak al
              teslimatAdresi = order.teslimatAdresi || '';
            }

            const activity = {
              id: order.id.toString(),
              courierId: order.teslimAlanKuryeId || order.teslimEdenKuryeId || '', // API camelCase
              courierName: 'Courier', // This value is enriched from Firebase
              orderId: order.id.toString(),
              orderNumber: order.siparisId,
              customerName: order.kullaniciAdi,
              customerPhone: telefonNumarasi,
              deliveryAddress: teslimatAdresi,
              musteriKonumu: order.musteriKonumu, // API camelCase
              orderType: 'ecommerce' as const,
              status: order.siparisDurumu === 'Teslim Edildi' ? 'delivered' as const : 
                     order.siparisDurumu === 'Kuryede' ? 'taken' as const : 
                     'cancelled' as const,
              takenAt: order.teslimAlmaZamani, // API camelCase
              deliveredAt: order.teslimEtmeZamani, // API camelCase
              deliveryDuration: order.teslimSuresi, // API camelCase
              totalAmount: order.toplamTutar,
              branchName: order.branchName,
              currentLocation: teslimatAdresi
            };

            console.log('📋 [DEBUG] Final activity objesi:', {
              siparisId: activity.orderNumber,
              deliveryAddress: activity.deliveryAddress,
              musteriKonumu: activity.musteriKonumu,
              deliveryAddressLength: activity.deliveryAddress?.length,
              musteriKonumuLength: activity.musteriKonumu?.length
            });

            return activity;
          });

          // Tarihe göre sırala (en yeni en üstte)
          activitiesData.sort((a, b) => {
            const dateA = new Date(a.takenAt || a.deliveredAt || 0);
            const dateB = new Date(b.takenAt || b.deliveredAt || 0);
            return dateB.getTime() - dateA.getTime();
          });

          // Kurye isimlerini Firebase'den çek
          await enrichWithCourierNames(activitiesData);

          setActivities(activitiesData);
          
          // Özet verileri hesapla
          const summariesData = calculateCourierSummaries(activitiesData);
          setCourierSummaries(summariesData);
          
          // Harita için mock kurye konumları oluştur (gerçek konum verisi olmadığı için)
          const locationsForMap: CourierLocation[] = summariesData.map((summary, index) => ({
            courierId: summary.courierId,
            courierName: summary.courierName,
            latitude: 40.8438 + (Math.random() - 0.5) * 0.02, // Düzce çevresinde rastgele
            longitude: 31.1565 + (Math.random() - 0.5) * 0.02,
            status: summary.status,
            currentOrder: summary.activeOrders > 0 ? `Active: ${summary.activeOrders}` : undefined,
            lastUpdate: new Date().toISOString(),
            branchName: summary.branchName
          }));
          setCourierLocations(locationsForMap);
          
          console.log('✅ [TRACKING] Kurye aktiviteleri işlendi:', {
            activities: activitiesData.length,
            summaries: summariesData.length,
            locations: locationsForMap.length
          });
          
          } else {
            console.log('📭 [TRACKING] Kurye aktivitesi bulunamadı');
            setActivities([]);
            setCourierSummaries([]);
          }
        
      } catch (error) {
        console.error('❌ [TRACKING] Kurye aktiviteleri yüklenirken hata:', error);
        
        // Hata durumunda boş liste göster
        setActivities([]);
        setCourierSummaries([]);
        setCourierLocations([]);
      } finally {
        setLoading(false);
      }
    };

    loadActivities();
  }, [companyId]);

  // Filtrelenmiş aktiviteler - Personnel.tsx'teki gibi güncellendi
  useEffect(() => {
    console.log('🔍 [FILTER] Filtreleme başlatılıyor:', {
      totalActivities: activities.length,
      searchTerm,
      statusFilter,
      courierFilter,
      dateFilter
    });
    
    let filtered = activities;

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(activity =>
        activity.courierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.customerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log('🔍 [FILTER] Arama filtresi sonrası:', filtered.length);
    }

    // Durum filtresi
    if (statusFilter !== 'all') {
      filtered = filtered.filter(activity => activity.status === statusFilter);
      console.log('🔍 [FILTER] Durum filtresi sonrası:', filtered.length);
    }

    // Kurye filtresi
    if (courierFilter !== 'all') {
      filtered = filtered.filter(activity => activity.courierId === courierFilter);
      console.log('🔍 [FILTER] Kurye filtresi sonrası:', filtered.length);
    }

    // Tarih filtresi (daha esnek implementasyon)
    if (dateFilter === 'today') {
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      
      filtered = filtered.filter(activity => {
        const activityDate = new Date(activity.takenAt || activity.deliveredAt || 0);
        return activityDate >= todayStart && activityDate < todayEnd;
      });
    } else if (dateFilter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStart = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
      const yesterdayEnd = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate() + 1);
      
      filtered = filtered.filter(activity => {
        const activityDate = new Date(activity.takenAt || activity.deliveredAt || 0);
        return activityDate >= yesterdayStart && activityDate < yesterdayEnd;
      });
    } else if (dateFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      filtered = filtered.filter(activity => {
        const activityDate = new Date(activity.takenAt || activity.deliveredAt || 0);
        return activityDate >= weekAgo;
      });
    } else if (dateFilter === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      
      filtered = filtered.filter(activity => {
        const activityDate = new Date(activity.takenAt || activity.deliveredAt || 0);
        return activityDate >= monthAgo;
      });
    }
    // 'all' seçeneğinde filtreleme yapma

    console.log('✅ [FILTER] Final filtrelenmiş aktiviteler:', filtered.length);
    setFilteredActivities(filtered);
  }, [activities, searchTerm, statusFilter, courierFilter, dateFilter]);

  // Yardımcı fonksiyonlar
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'taken':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return t('courierTracking.status.delivered');
      case 'taken':
        return t('courierTracking.status.withCourier');
      case 'cancelled':
        return t('courierTracking.status.cancelled');
      default:
        return t('courierTracking.defaults.unknown');
    }
  };

  const getOrderTypeText = (type: string) => {
    switch (type) {
      case 'restaurant':
        return t('courierTracking.orderTypes.restaurant');
      case 'ecommerce':
        return t('courierTracking.orderTypes.ecommerce');
      default:
        return t('courierTracking.defaults.unknown');
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Event handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleCourierFilterChange = (event: SelectChangeEvent) => {
    setCourierFilter(event.target.value);
    setPage(0);
  };

  const handleDateFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleOpenMap = () => {
    setMapModalOpen(true);
  };

  // Müşteri konumu modalını aç
  const handleOpenCustomerLocationModal = (activity: CourierActivity) => {
    if (activity.musteriKonumu) {
      try {
        console.log('🗺️ [LOCATION] Müşteri konumu parse ediliyor:', activity.musteriKonumu);
        const location = JSON.parse(activity.musteriKonumu);
        
        // Format: {"latitude":40.844187259871845,"longitude":31.172611713409427}
        if (location.latitude && location.longitude) {
          setSelectedCustomerLocation({
            latitude: location.latitude,
            longitude: location.longitude,
            customerName: activity.customerName,
            orderNumber: activity.orderNumber
          });
          setCustomerLocationModalOpen(true);
          console.log('✅ [LOCATION] Müşteri konumu modalı açıldı:', location);
        } else {
          console.warn('⚠️ [LOCATION] Latitude veya longitude bulunamadı:', location);
        }
      } catch (error) {
        console.error('❌ [LOCATION] Müşteri konumu parse edilemedi:', error, activity.musteriKonumu);
      }
    } else {
      console.warn('⚠️ [LOCATION] Müşteri konumu verisi yok:', activity.orderNumber);
    }
  };

  // Basit marker renkleri - varsayılan ikonları kullan
  const getMarkerColor = (status: string) => {
    switch (status) {
      case 'active':
        return '🟢';
      case 'busy':
        return '🟡';
      case 'offline':
        return '🔴';
      default:
        return '⚪';
    }
  };

  if (loading) {
    return (
      <Container 
        maxWidth={false} 
        sx={{ 
          mt: 4, 
          mb: 4, 
          px: { xs: 1, sm: 2, md: 3 },
          height: 'calc(100vh - 64px)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('courierTracking.loading')}
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container 
        maxWidth={false} 
        sx={{ 
          mt: 4, 
          mb: 4, 
          px: { xs: 1, sm: 2, md: 3 },
          height: 'calc(100vh - 64px)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          {t('courierTracking.actions.refreshPage')}
        </Button>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        mt: 4, 
        mb: 4, 
        px: { xs: 1, sm: 2, md: 3 },
        height: 'calc(100vh - 64px)', // Header yüksekliğini çıkar
        overflowY: 'auto', // Sayfa kaydırma çubuğu
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}
    >
      <Helmet>
        <title>{t('courierTracking.pageTitle')}</title>
      </Helmet>

      {/* Başlık ve Geri Butonu */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={4}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Box display="flex" alignItems="center">
          <IconButton 
            onClick={onBack}
            sx={{ mr: 1, color: theme.palette.primary.main }}
            size="large"
          >
            <BackIcon />
          </IconButton>
          <TrackingIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.primary.main, mr: 2 }} />
          <Typography 
            variant="h4" 
            component="h1" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
          >
            {t('courierTracking.title')}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            {t('courierTracking.actions.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<MapIcon />}
            onClick={handleOpenMap}
          >
            {t('courierTracking.actions.showOnMap')}
          </Button>
        </Box>
      </Box>

      {/* Özet Kartları */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('courierTracking.stats.activeCouriers')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="success.main">
                    {courierSummaries.filter(c => c.status === 'active' || c.status === 'busy').length}
                  </Typography>
                </Box>
                <DeliveryIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('courierTracking.stats.ongoingDeliveries')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
                    {activities.filter(a => a.status === 'taken').length}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('courierTracking.stats.deliveredToday')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="info.main">
                    {activities.filter(a => a.status === 'delivered').length}
                  </Typography>
                </Box>
                <CompletedIcon sx={{ fontSize: 40, color: theme.palette.info.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('courierTracking.stats.averageDeliveryTime')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="primary.main">
                    {courierSummaries.length > 0 
                      ? Math.round(courierSummaries.reduce((sum, c) => sum + c.averageDeliveryTime, 0) / courierSummaries.length)
                      : 0
                    }d
                  </Typography>
                </Box>
                <TimeIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtreler */}
      <Paper sx={{ borderRadius: 2, boxShadow: 2, mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('courierTracking.filters.searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('courierTracking.filters.status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('courierTracking.filters.status')}
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">{t('courierTracking.filters.all')}</MenuItem>
                <MenuItem value="taken">{t('courierTracking.status.withCourier')}</MenuItem>
                <MenuItem value="delivered">{t('courierTracking.status.delivered')}</MenuItem>
                <MenuItem value="cancelled">{t('courierTracking.status.cancelled')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('courierTracking.filters.courier')}</InputLabel>
              <Select
                value={courierFilter}
                label={t('courierTracking.filters.courier')}
                onChange={handleCourierFilterChange}
              >
                <MenuItem value="all">{t('courierTracking.filters.allCouriers')}</MenuItem>
                {courierSummaries.map((courier) => (
                  <MenuItem key={courier.courierId} value={courier.courierId}>
                    {courier.courierName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('courierTracking.filters.date')}</InputLabel>
              <Select
                value={dateFilter}
                label={t('courierTracking.filters.date')}
                onChange={handleDateFilterChange}
              >
                <MenuItem value="all">{t('courierTracking.filters.all')}</MenuItem>
                <MenuItem value="today">{t('courierTracking.filters.today')}</MenuItem>
                <MenuItem value="yesterday">{t('courierTracking.filters.yesterday')}</MenuItem>
                <MenuItem value="week">{t('courierTracking.filters.thisWeek')}</MenuItem>
                <MenuItem value="month">{t('courierTracking.filters.thisMonth')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <FilterIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {t('courierTracking.filters.results', { count: filteredActivities.length })}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Aktivite Listesi */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Box p={3}>
          <Typography variant="h6" component="h2" fontWeight="bold" mb={2}>
            {t('courierTracking.activities.title', { count: filteredActivities.length })}
          </Typography>
          
          {filteredActivities.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('courierTracking.activities.empty')}
            </Alert>
          ) : (
            <>
              {/* 📱 MOBİL: Kart Görünümü */}
              {isMobile ? (
                <Grid container spacing={2}>
                  {paginatedActivities.map((activity) => (
                    <Grid item xs={12} sm={6} key={activity.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          borderRadius: 2,
                          borderLeft: 4,
                          borderLeftColor: activity.status === 'delivered' ? 'success.main' : 
                                          activity.status === 'taken' ? 'warning.main' : 'error.main',
                          '&:hover': { boxShadow: 3 }
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          {/* Üst: Kurye + Sipariş No + Durum */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                              <Avatar sx={{ 
                                width: 36, 
                                height: 36, 
                                bgcolor: theme.palette.primary.main,
                                fontSize: 14
                              }}>
                                {activity.courierName.charAt(0)}
                              </Avatar>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                  {activity.courierName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  #{activity.orderNumber}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip 
                                label={getStatusText(activity.status)}
                                color={getStatusColor(activity.status) as any}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 22 }}
                              />
                              {activity.musteriKonumu && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenCustomerLocationModal(activity)}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <LocationIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          {/* Müşteri Bilgileri */}
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="body2" fontWeight="medium">
                              👤 {activity.customerName}
                            </Typography>
                            {activity.customerPhone && (
                              <Typography variant="caption" color="text.secondary">
                                📞 {activity.customerPhone}
                              </Typography>
                            )}
                          </Box>

                          {/* İstatistikler 2x2 Grid */}
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t('courierTracking.cards.taken')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" noWrap>
                                  {formatDateTime(activity.takenAt).split(' ')[1] || '-'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t('courierTracking.cards.delivered')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" noWrap>
                                  {formatDateTime(activity.deliveredAt).split(' ')[1] || '-'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t('courierTracking.cards.duration')}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="info.main">
                                  {formatDuration(activity.deliveryDuration)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t('courierTracking.cards.amount')}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="success.main">
                                  ₺{activity.totalAmount.toFixed(2)}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Teslimat Adresi */}
                          {activity.deliveryAddress && (
                            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.3 }} />
                              <Typography variant="caption" color="text.secondary" sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}>
                                {activity.deliveryAddress}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                /* 💻 MASAÜSTÜ: Tablo Görünümü */
                <TableContainer component={Paper} sx={{ 
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 120
                        }}>{t('courierTracking.table.courier')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 100
                        }}>{t('courierTracking.table.orderNo')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 120
                        }}>{t('courierTracking.table.customer')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 80
                        }}>{t('courierTracking.table.status')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 120
                        }}>{t('courierTracking.table.courierReceived')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 120
                        }}>{t('courierTracking.table.courierDelivered')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 120
                        }}>{t('courierTracking.table.pickupTime')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 120
                        }}>{t('courierTracking.table.deliveryTime')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 80
                        }}>{t('courierTracking.table.deliveryDuration')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 80
                        }}>{t('courierTracking.table.amount')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 150
                        }}>{t('courierTracking.table.deliveryAddress')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 0.7,
                          minWidth: 80
                        }}>{t('courierTracking.table.location')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedActivities.map((activity) => {
                        console.log('🎨 [RENDER] Activity render ediliyor:', {
                          orderNumber: activity.orderNumber,
                          deliveryAddress: activity.deliveryAddress,
                          musteriKonumu: activity.musteriKonumu,
                          hasDeliveryAddress: !!activity.deliveryAddress,
                          hasMusteriKonumu: !!activity.musteriKonumu
                        });
                        
                        return (
                        <TableRow 
                          key={activity.id} 
                          hover
                          sx={{ 
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            '& .MuiTableCell-root': { 
                              py: 0.1, // Personnel.tsx'teki gibi küçük padding
                              height: 28, // Personnel.tsx'teki gibi sabit yükseklik
                              fontSize: '0.775rem' // Personnel.tsx'teki gibi küçük font
                            }
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar sx={{ 
                                width: 28, 
                                height: 28, 
                                mr: 1, 
                                bgcolor: theme.palette.primary.main,
                                fontSize: 12
                              }}>
                                {activity.courierName.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {activity.courierName}
                                </Typography>
                                {activity.branchName && (
                                  <Typography variant="caption" color="text.secondary">
                                    {activity.branchName}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {activity.orderNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('courierTracking.orderTypes.ecommerce')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {activity.customerName}
                            </Typography>
                            {activity.customerPhone && (
                              <Typography variant="caption" color="text.secondary">
                                {activity.customerPhone}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getStatusText(activity.status)}
                              color={getStatusColor(activity.status) as any}
                              size="small"
                              sx={{ fontSize: '10px', height: 20 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {activity.courierName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {activity.status === 'delivered' ? activity.courierName : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(activity.takenAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(activity.deliveredAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium" color="info.main">
                              {formatDuration(activity.deliveryDuration)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              ₺{activity.totalAmount.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={activity.deliveryAddress || t('courierTracking.table.noAddressInfo')}>
                              <Typography variant="caption" color="text.secondary" sx={{
                                maxWidth: '140px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {activity.deliveryAddress || t('courierTracking.table.noAddressInfo')}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {activity.musteriKonumu ? (
                              <Tooltip title={t('courierTracking.tooltips.showCustomerLocation')}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    console.log('🗺️ [DEBUG] Konum ikonu tıklandı:', activity.orderNumber, activity.musteriKonumu);
                                    handleOpenCustomerLocationModal(activity);
                                  }}
                                  sx={{ 
                                    color: 'primary.main',
                                    '&:hover': {
                                      backgroundColor: 'primary.light',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  <LocationIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })}
                      {paginatedActivities.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">
                              {t('courierTracking.activities.noActivityFound')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {/* Sayfalama - Personnel.tsx'teki gibi */}
              <TablePagination
                component="div"
                count={filteredActivities.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={isMobile ? [10, 25, 50] : [25, 50, 100]}
                labelRowsPerPage={isMobile ? t('courierTracking.pagination.page') : t('courierTracking.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => 
                  isMobile ? `${from}-${to} / ${count}` : t('courierTracking.pagination.displayedRows', { from, to, count })
                }
                sx={{ mt: 1 }}
              />
            </>
          )}
        </Box>
      </Paper>

      {/* Map Modal - Leaflet Harita */}
      <Dialog
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, height: '85vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <MapIcon color="primary" />
          <Typography variant="h6">{t('courierTracking.mapModal.title')}</Typography>
          <Box ml="auto" display="flex" gap={1} alignItems="center">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="body2">🟢</Typography>
              <Typography variant="caption">{t('courierTracking.status.active')}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="body2">🟡</Typography>
              <Typography variant="caption">{t('courierTracking.status.busy')}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="body2">🔴</Typography>
              <Typography variant="caption">{t('courierTracking.status.offline')}</Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, height: '100%' }}>
          <Box sx={{ height: '100%', width: '100%' }}>
            <MapContainer
              center={[40.8438, 31.1565]} // Düzce merkez koordinatları
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {courierLocations.map((courier) => (
                <Marker
                  key={courier.courierId}
                  position={[courier.latitude, courier.longitude]}
                >
                  <Popup>
                    <Box sx={{ minWidth: 200 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="h6" fontWeight="bold">
                          {getMarkerColor(courier.status)} {courier.courierName}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip 
                          label={courier.status === 'active' ? t('courierTracking.status.active') : 
                                courier.status === 'busy' ? t('courierTracking.status.busy') : t('courierTracking.status.offline')}
                          color={courier.status === 'active' ? 'success' : 
                                courier.status === 'busy' ? 'warning' : 'error'}
                          size="small"
                        />
                      </Box>
                      
                      {courier.branchName && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          📍 {courier.branchName}
                        </Typography>
                      )}
                      
                      {courier.currentOrder && (
                        <Typography variant="body2" color="primary" gutterBottom>
                          🚚 {t('courierTracking.mapModal.activeOrder')}: {courier.currentOrder}
                        </Typography>
                      )}
                      
                      <Typography variant="caption" color="text.secondary">
                        {t('courierTracking.mapModal.lastUpdate')}: {new Date(courier.lastUpdate).toLocaleString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: '2-digit',
                          month: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            {t('courierTracking.mapModal.totalLocations', { count: courierLocations.length })}
          </Typography>
          <Button onClick={() => setMapModalOpen(false)} variant="contained">
            {t('courierTracking.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Müşteri Konumu Modalı */}
      <Dialog
        open={customerLocationModalOpen}
        onClose={() => setCustomerLocationModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationIcon color="primary" />
            <Typography variant="h6">
              {t('courierTracking.customerLocationModal.title', { name: selectedCustomerLocation?.customerName || '' })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('courierTracking.customerLocationModal.orderNo')}: {selectedCustomerLocation?.orderNumber}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedCustomerLocation && (
            <Box sx={{ height: '400px', width: '100%', mt: 1 }}>
              <MapContainer
                center={[selectedCustomerLocation.latitude, selectedCustomerLocation.longitude]}
                zoom={16}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker 
                  position={[selectedCustomerLocation.latitude, selectedCustomerLocation.longitude]}
                >
                  <Popup>
                    <Box textAlign="center">
                      <Typography variant="body2" fontWeight="bold">
                        {selectedCustomerLocation.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('courierTracking.customerLocationModal.order')}: {selectedCustomerLocation.orderNumber}
                      </Typography>
                      <br />
                      <Typography variant="caption">
                        📍 {selectedCustomerLocation.latitude.toFixed(6)}, {selectedCustomerLocation.longitude.toFixed(6)}
                      </Typography>
                    </Box>
                  </Popup>
                </Marker>
              </MapContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerLocationModalOpen(false)} variant="contained">
            {t('courierTracking.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CourierTracking;
